import { lettersGo } from "./coldLetterLaw.js";
import { COUNTRY_CODES, GENERIC_REFUSALS, cleanProspectDomain } from "./customers.js";
import { letterFrame } from "./letter.js";
import { isFreeMailbox, mailDomain } from "./mailbox.js";

/**
 * A list somebody brings: named people at companies, in a CSV as people export them
 * ([24](../../../docs/24-everything-reach-does.md) §the three that were never ported, item 1; [23](../../../docs/23-customers.md)
 * §a list somebody brings).
 *
 * Overbill's Reach had this as `ReachCsv`, for a list of companies, and two lessons came with it from real files:
 * **a header is matched against what people call a column, never against one spelling we publish**, and **nothing is
 * guessed from position** — a file with no column that reads as the company is refused with the words to use,
 * because guessing the company from column order is how an import turns a phone number into a company. A CSV exported
 * from Excel in a French or Spanish locale is separated by semicolons, so the separator is read off the header rather
 * than assumed. Every column nothing here reads is named back, so the import says what it read and what it left.
 *
 * What changed on the way here (2026-10-06): a row is a **person at a company**, not a company. A business that sells
 * to large companies writes to the one who decides — the head of logistics, of operations, of finance — and not to an
 * `info@` address, and such a list is what a contact directory exports. So the vocabulary gained the person's name and
 * role and the address's verification status, and *name*, *nom*, *nombre* and *nome*, which Reach read as the
 * company's, are the person's here: beside a first-name column, *nom* is a surname.
 *
 * Pure, and the whole of the decision about a row is here: the database only supplies what this workspace already
 * holds (`sortList`'s `known`), so every skip has a reason that can be proved without one.
 */

/** What a column can be read as. */
export const LIST_FIELDS = [
  "company", "firstName", "lastName", "fullName", "role", "email", "emailStatus",
  "website", "domain", "country", "city", "employees", "industry", "phone",
] as const;
export type ListField = (typeof LIST_FIELDS)[number];

/** How each field is named back on the screen. */
export const LIST_FIELD_WORDS: Record<ListField, string> = {
  company: "Company", firstName: "First name", lastName: "Last name", fullName: "Name", role: "Role",
  email: "Email", emailStatus: "Email status", website: "Website", domain: "Domain", country: "Country",
  city: "City", employees: "Employees", industry: "Industry", phone: "Phone",
};

/**
 * What people call each column, most specific first.
 *
 * Matched whole, after `headerKey` has taken off case, accents and punctuation — never as a substring, because
 * *Secondary Email*, *Personal Email* and *Email Bounced* are columns a directory exports beside *Email* and none of
 * them is the address to write to. Where a file has two columns for one field — *Company Name for Emails* and
 * *Company*, *Company Country* and *Country* — each row takes the first that has something in it, in this order.
 */
const VOCABULARY: Record<ListField, readonly string[]> = {
  company: [
    "company name for emails", "company name", "company", "account name", "organisation", "organization",
    "organisation name", "organization name", "business name", "business", "trading name", "employer",
    "société", "raison sociale", "entreprise", "nom de l'entreprise", "nom entreprise",
    "empresa", "razón social", "nombre de la empresa", "compañía", "azienda", "ragione sociale",
    "الشركة", "اسم الشركة",
  ],
  firstName: ["first name", "firstname", "given name", "forename", "prénom", "nombre", "nome", "الاسم الأول"],
  lastName: ["last name", "lastname", "surname", "family name", "nom de famille", "nom", "apellidos", "apellido", "cognome", "اسم العائلة"],
  fullName: ["full name", "name", "contact name", "contact", "person", "nom complet", "nombre completo", "nome completo", "الاسم الكامل", "الاسم"],
  role: [
    "title", "job title", "position", "role", "job role", "job", "fonction", "poste", "titre", "intitulé du poste",
    "cargo", "puesto", "ruolo", "المسمى الوظيفي", "الوظيفة",
  ],
  email: [
    "email", "e-mail", "email address", "e-mail address", "work email", "business email", "mail", "courriel",
    "adresse e-mail", "adresse email", "adresse mail", "correo", "correo electrónico", "email profesional",
    "البريد الإلكتروني", "البريد",
  ],
  emailStatus: [
    "email status", "email verification status", "email verification", "verification status", "email verified",
    "statut de l'email", "statut email", "statut e-mail", "estado del correo", "estado del email", "verificación",
  ],
  website: [
    "website", "web site", "company website", "website url", "site", "url", "site web", "site internet",
    "sitio web", "página web", "sito web", "الموقع الإلكتروني", "الموقع",
  ],
  domain: ["domain", "company domain", "website domain", "domaine", "dominio", "النطاق"],
  country: ["company country", "country", "pays", "país", "paese", "الدولة", "البلد"],
  city: ["company city", "city", "town", "ville", "ciudad", "localidad", "città", "المدينة"],
  employees: [
    "# employees", "employees", "number of employees", "employee count", "headcount", "company size", "size",
    "effectif", "nombre de salariés", "empleados", "número de empleados", "dipendenti", "عدد الموظفين",
  ],
  industry: ["industry", "sector", "secteur", "secteur d'activité", "industrie", "industria", "settore", "القطاع", "الصناعة"],
  phone: ["company phone", "phone", "telephone", "téléphone", "tel", "teléfono", "telefono", "móvil", "الهاتف"],
};

/**
 * A header as it is compared: lower case, without accents, and with every run of punctuation and spaces one space.
 * `#` is kept, because *# Employees* is how a directory names a headcount.
 */
export function headerKey(raw: string): string {
  return raw.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase().replace(/[^\p{L}\p{N}#]+/gu, " ").trim();
}

/** Every word in the vocabulary, keyed as it is compared, with the field it names and how specific it is. */
const LOOKUP: ReadonlyMap<string, { field: ListField; rank: number }> = new Map(
  LIST_FIELDS.flatMap((field) => VOCABULARY[field].map((word, rank) => [headerKey(word), { field, rank }] as const)),
);

/** The vocabulary as the tests read it: every compared word and the field it belongs to. */
export const listVocabulary = (): Array<{ word: string; field: ListField }> =>
  LIST_FIELDS.flatMap((field) => VOCABULARY[field].map((word) => ({ word: headerKey(word), field })));

/** The most rows one import reads. A month of a directory's export for one business is a few hundred. */
export const LIST_MAX_ROWS = 2000;
/** And the most characters, which is about that many rows of a wide export. */
export const LIST_MAX_CHARS = 3_000_000;

/* ---------------------------------------------------------------------------------------- the file itself ---- */

export type ListDelimiter = "," | ";" | "\t";

/**
 * Which character separates the cells, read off the header line.
 *
 * Excel writes a "CSV" with semicolons wherever a comma is the decimal point — France, Spain, most of Europe — and a
 * reader that assumes commas reads such a file as one column with the whole row in it. The header has no numbers in
 * it, so whichever separator it holds most of, outside quotes, is the file's. A tab is a third, for a list pasted out
 * of a spreadsheet and saved as text.
 */
export function delimiterOf(text: string): ListDelimiter {
  const counts: Record<ListDelimiter, number> = { ",": 0, ";": 0, "\t": 0 };
  let quoted = false;
  for (const ch of text) {
    if (ch === "\"") quoted = !quoted;
    else if (!quoted && (ch === "\n" || ch === "\r")) break;
    else if (!quoted && (ch === "," || ch === ";" || ch === "\t")) counts[ch] += 1;
  }
  const best = (Object.keys(counts) as ListDelimiter[]).reduce((a, b) => (counts[b] > counts[a] ? b : a), ",");
  return counts[best] > 0 ? best : ",";
}

/**
 * The cells of a CSV, row by row (RFC 4180, read leniently).
 *
 * Quoted cells may hold the separator, a line break and a doubled quote; a byte-order mark at the start is dropped,
 * as Excel writes one; a row with nothing in it is not a row. A stray quote in the middle of an unquoted cell is kept
 * as written rather than refused — a list is somebody's working file, and one odd cell is not a reason to read none.
 */
export function csvRows(text: string): { delimiter: ListDelimiter; rows: string[][] } {
  const body = text.replace(/^﻿/, "");
  const delimiter = delimiterOf(body);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const endRow = () => { row.push(cell); rows.push(row); row = []; cell = ""; };
  for (let i = 0; i < body.length; i++) {
    const ch = body[i]!;
    if (quoted) {
      if (ch !== "\"") { cell += ch; continue; }
      if (body[i + 1] === "\"") { cell += "\""; i += 1; } else quoted = false;
      continue;
    }
    if (ch === "\"" && cell.trim() === "") { cell = ""; quoted = true; continue; }
    if (ch === delimiter) { row.push(cell); cell = ""; continue; }
    if (ch === "\r") { if (body[i + 1] !== "\n") endRow(); continue; }
    if (ch === "\n") { endRow(); continue; }
    cell += ch;
  }
  if (cell !== "" || row.length) endRow();
  return { delimiter, rows: rows.filter((r) => r.some((c) => c.trim() !== "")) };
}

/* ------------------------------------------------------------------------------------------ reading a list ---- */

/** One row as the list has it, every field read and nothing yet decided. */
export interface ListRow {
  /** Which row of the list this is, the header not counted, so a person can find it in their own file. */
  row: number;
  company: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  /** What the list says of the address: `null` when it has no such column, which is not the same as an empty cell. */
  emailStatus: string | null;
  website: string;
  domain: string;
  country: string;
  city: string;
  employees: string;
  industry: string;
  phone: string;
}

export type ListRead =
  | { ok: false; says: string }
  | {
    ok: true;
    delimiter: ListDelimiter;
    /** Each field the list has, and the column or columns it is read from, most specific first. */
    columns: Array<{ field: ListField; headers: string[] }>;
    /** Every column nothing here reads, by the name the file gives it. */
    ignored: string[];
    rows: ListRow[];
  };

/** The words that would have been read as each of the two columns a list cannot do without. */
const ASK_FOR: Record<"company" | "email", string> = {
  company: "Company (or Company name, Organisation, Société, Entreprise, Empresa)",
  email: "Email (or E-mail, Email address, Courriel, Correo electrónico)",
};

/**
 * Read a list into rows, or say why it cannot be read.
 *
 * Refused, with the words to use, when there is no column that reads as the company or none that reads as an email:
 * those two are the whole of what a letter needs, and neither is ever guessed from where a column happens to sit.
 */
export function readList(text: string): ListRead {
  if (text.length > LIST_MAX_CHARS) {
    return { ok: false, says: `This file is too large to read at once. Split it into parts of up to ${LIST_MAX_ROWS.toLocaleString("en-GB")} rows and import each one.` };
  }
  const { delimiter, rows } = csvRows(text);
  const header = rows[0];
  if (!header) return { ok: false, says: "This file is empty." };
  if (rows.length < 2) return { ok: false, says: "This file has a header and nothing under it." };
  if (rows.length - 1 > LIST_MAX_ROWS) {
    return { ok: false, says: `This list has ${(rows.length - 1).toLocaleString("en-GB")} rows, and up to ${LIST_MAX_ROWS.toLocaleString("en-GB")} are read at a time. Split it and import each part.` };
  }

  // Which column is which, by name alone.
  const found = new Map<ListField, Array<{ index: number; rank: number; header: string }>>();
  const ignored: string[] = [];
  header.forEach((raw, index) => {
    const name = raw.trim();
    const hit = LOOKUP.get(headerKey(name));
    if (!hit) { ignored.push(name || `a column with no heading (column ${index + 1})`); return; }
    found.set(hit.field, [...(found.get(hit.field) ?? []), { index, rank: hit.rank, header: name }]);
  });
  for (const list of found.values()) list.sort((a, b) => a.rank - b.rank);

  const missing = (["company", "email"] as const).filter((f) => !found.has(f));
  if (missing.length) {
    const words = missing.map((f) => ASK_FOR[f]).join(", and one called ");
    return {
      ok: false,
      says: `This list has no column that reads as ${missing.map((f) => (f === "company" ? "the company" : "an email address")).join(" or as ")}, and nothing is guessed from where a column sits. Call one column ${words}, and import it again.`,
    };
  }

  const pick = (cells: readonly string[], field: ListField): string => {
    for (const c of found.get(field) ?? []) {
      const v = (cells[c.index] ?? "").trim();
      if (v) return v;
    }
    return "";
  };

  const out: ListRow[] = rows.slice(1).map((cells, i) => {
    const full = pick(cells, "fullName").replace(/\s+/g, " ");
    const [fullFirst = "", ...fullRest] = full ? full.split(" ") : [];
    return {
      row: i + 1,
      company: pick(cells, "company").replace(/\s+/g, " "),
      firstName: pick(cells, "firstName") || fullFirst,
      lastName: pick(cells, "lastName") || fullRest.join(" "),
      role: pick(cells, "role").replace(/\s+/g, " "),
      email: pick(cells, "email"),
      emailStatus: found.has("emailStatus") ? pick(cells, "emailStatus") : null,
      website: pick(cells, "website"),
      domain: pick(cells, "domain"),
      country: pick(cells, "country"),
      city: pick(cells, "city"),
      employees: pick(cells, "employees"),
      industry: pick(cells, "industry"),
      phone: pick(cells, "phone"),
    };
  });

  return {
    ok: true, delimiter,
    columns: LIST_FIELDS.filter((f) => found.has(f)).map((field) => ({ field, headers: found.get(field)!.map((c) => c.header) })),
    ignored,
    rows: out,
  };
}

/* ------------------------------------------------------------------------------------------- what is kept ---- */

/** Why a row is not added. Said before anything is, with how many of each. */
export const LIST_SKIPS = [
  "no_company", "no_email", "bad_email", "not_verified", "free_mailbox", "suppressed",
  "known_address", "known_company", "twice_in_file", "company_in_file", "no_country",
] as const;
export type ListSkip = (typeof LIST_SKIPS)[number];

export const LIST_SKIP_WORDS: Record<ListSkip, string> = {
  no_company: "No company name",
  no_email: "No email address",
  bad_email: "An email address that is not one",
  not_verified: "The list does not mark the address verified, and only a verified one is written to: a guessed address bounces, and bounces close a sending account",
  free_mailbox: `${GENERIC_REFUSALS.find((g) => g.code === "free_mailbox")!.says}: a company's own address is the one to write to`,
  suppressed: "An address you never write to again: it asked you to stop, bounced, or was stopped by hand",
  known_address: "An address already on your list",
  known_company: "A company already on your list",
  twice_in_file: "The same address twice in this list",
  company_in_file: "A second person at a company already in this list: one letter goes to each company",
  no_country: "No country, and none chosen for the rows that do not say",
};

/** What a verified address is called, in the words directories and their exports use. Anything else is not one. */
const VERIFIED = new Set(["verified", "valid", "deliverable", "safe", "ok", "vérifié", "verificado", "válido", "valide", "موثق"].map(headerKey));

/** Whether the list says this address is verified. *Unverified*, *Likely to engage*, *Guessed* and an empty cell are not. */
export const saysVerified = (status: string): boolean => VERIFIED.has(headerKey(status));

/** An address as it is compared and stored: lower case, without `mailto:` or angle brackets. Null when it is not one. */
export function listEmail(raw: string): string | null {
  const t = raw.trim().replace(/^mailto:/i, "").replace(/^<|>$/g, "").trim().toLowerCase();
  return /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:".]{2,}$/.test(t) ? t : null;
}

/**
 * A first name a letter can greet somebody by, or null when the cell holds something else.
 *
 * Letters, the marks and joins names have (O'Neil, Jean-Luc, Mª), and nothing that is plainly not a name — an initial,
 * a dash, a number. A name an export shouted in capitals, or wrote all in small letters, is written the way its owner
 * signs it, as the mailbox's own name is (22, R-3); a name with capitals somewhere in it is left exactly as it is.
 */
export function firstNameOf(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, " ");
  if (t.length < 2 || t.length > 40) return null;
  if (!/^\p{L}[\p{L}\p{M}'’ .-]*$/u.test(t) || t.replace(/[^\p{L}]/gu, "").length < 2) return null;
  const uniform = t === t.toUpperCase() || t === t.toLowerCase();
  return uniform ? t.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (_, sep: string, l: string) => sep + l.toUpperCase()) : t;
}

/** Where a list came from, as the person names it — the directory, the fair, the event — or null when it is not a name. */
export const LIST_SOURCE_MAX = 40;
export function listSourceOf(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, " ");
  if (t.length < 2 || t.length > LIST_SOURCE_MAX) return null;
  return /^[\p{L}\p{N}][\p{L}\p{N}\p{M} .&'’()+-]*$/u.test(t) ? t : null;
}

/** Country names a list writes that the platform's own names do not cover. */
const COUNTRY_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ["UK", "GB"], ["U.K.", "GB"], ["Great Britain", "GB"], ["Britain", "GB"], ["England", "GB"], ["Scotland", "GB"],
  ["Wales", "GB"], ["Northern Ireland", "GB"], ["UAE", "AE"], ["U.A.E.", "AE"], ["Emirates", "AE"],
  ["USA", "US"], ["U.S.A.", "US"], ["United States of America", "US"], ["America", "US"], ["Holland", "NL"],
  ["Russia", "RU"], ["South Korea", "KR"], ["Czech Republic", "CZ"], ["Ivory Coast", "CI"], ["Turkey", "TR"],
];

let NAMES: Map<string, string> | null = null;

/**
 * Every country by every name a list is likely to write it in — the platform's own English, French and Spanish
 * names, and the handful above — built once. The platform rather than a table here, for the reason `countryName`
 * gives: a country that renames itself is not a pull request.
 */
function countryNames(): Map<string, string> {
  if (NAMES) return NAMES;
  const names = new Map<string, string>();
  for (const locale of ["en", "fr", "es"]) {
    let shown: Intl.DisplayNames | null = null;
    try { shown = new Intl.DisplayNames([locale], { type: "region" }); } catch { shown = null; }
    if (!shown) continue;
    for (const code of COUNTRY_CODES) {
      const name = shown.of(code);
      if (name && name !== code) names.set(headerKey(name), code);
    }
  }
  for (const [alias, code] of COUNTRY_ALIASES) names.set(headerKey(alias), code);
  NAMES = names;
  return names;
}

/** A country as a list writes it — *United Arab Emirates*, *Royaume-Uni*, *GB*, *UK* — as its two letters, or null. */
export function countryCodeOf(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (/^[A-Za-z]{2}$/.test(t)) {
    const code = t.toUpperCase() === "UK" ? "GB" : t.toUpperCase();
    return COUNTRY_CODES.includes(code) ? code : null;
  }
  return countryNames().get(headerKey(t)) ?? null;
}

/** One person to write to, as they would be added. */
export interface ListPerson {
  row: number;
  company: string;
  /** The company's website as the list gives it, or null; `domain` is the key it is known by either way. */
  website: string | null;
  domain: string;
  email: string;
  emailStatus: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  country: string;
  city: string | null;
  employees: string | null;
  industry: string | null;
  phone: string | null;
}

export interface ListSkipped { row: number; why: ListSkip; company: string; email: string; said: string | null }

/** What this workspace already holds, as `sortList` compares it: lower-case addresses and cleaned domains. */
export interface ListKnown {
  emails: ReadonlySet<string>;
  domains: ReadonlySet<string>;
  suppressed: ReadonlySet<string>;
}

/**
 * Which rows are added, and why each of the others is not.
 *
 * In this order, and the order is what a person is told: a row with nothing to write to first, then one the list
 * itself does not vouch for, then the product's own refusal of a free mailbox, then the suppression list — an address
 * that asked to be left alone is never added, whatever list it reappears on — and only then what this workspace and
 * this file already hold. One letter goes to each company, so a second person at the same company is skipped rather
 * than queued for a second letter to the same building.
 *
 * **When the list has an email-status column, only a verified address is kept.** A directory marks the addresses it
 * has not checked — *unverified*, *guessed*, *likely to engage* — and those are the ones that bounce; a list with no
 * such column is the person's own word, and is taken as it is.
 */
export function sortList(rows: readonly ListRow[], known: ListKnown, fallbackCountry: string | null): { add: ListPerson[]; skipped: ListSkipped[] } {
  const add: ListPerson[] = [];
  const skipped: ListSkipped[] = [];
  const emails = new Set<string>();
  const domains = new Set<string>();
  const fallback = fallbackCountry ? countryCodeOf(fallbackCountry) : null;

  for (const r of rows) {
    const skip = (why: ListSkip, said: string | null = null) => skipped.push({ row: r.row, why, company: r.company, email: r.email.trim(), said });
    if (!r.company) { skip("no_company"); continue; }
    if (!r.email.trim()) { skip("no_email"); continue; }
    const email = listEmail(r.email);
    if (!email) { skip("bad_email", r.email.trim()); continue; }
    if (r.emailStatus !== null && !saysVerified(r.emailStatus)) { skip("not_verified", r.emailStatus || null); continue; }
    if (isFreeMailbox(email)) { skip("free_mailbox"); continue; }
    if (known.suppressed.has(email)) { skip("suppressed"); continue; }
    if (known.emails.has(email)) { skip("known_address"); continue; }
    // The company is known by its website when the list gives one, and otherwise by its own address's domain —
    // which is the company's, because a free mailbox was refused above.
    const domain = cleanProspectDomain(r.website) ?? cleanProspectDomain(r.domain) ?? cleanProspectDomain(mailDomain(email) ?? "");
    if (!domain) { skip("bad_email", email); continue; }
    if (known.domains.has(domain)) { skip("known_company"); continue; }
    if (emails.has(email)) { skip("twice_in_file"); continue; }
    if (domains.has(domain)) { skip("company_in_file"); continue; }
    const country = countryCodeOf(r.country) ?? fallback;
    if (!country) { skip("no_country", r.country || null); continue; }

    emails.add(email);
    domains.add(domain);
    const site = cleanProspectDomain(r.website);
    add.push({
      row: r.row, company: r.company.slice(0, 200),
      website: site ? (/^https?:\/\//i.test(r.website.trim()) ? r.website.trim().slice(0, 500) : `https://${site}`) : null,
      domain, email, emailStatus: r.emailStatus ? r.emailStatus.slice(0, 40) : null,
      firstName: firstNameOf(r.firstName),
      lastName: r.lastName.trim().replace(/\s+/g, " ").slice(0, 80) || null,
      role: r.role.slice(0, 120) || null,
      country,
      city: r.city.trim().slice(0, 80) || null,
      employees: r.employees.trim().slice(0, 40) || null,
      industry: r.industry.trim().slice(0, 120) || null,
      phone: r.phone.trim().slice(0, 40) || null,
    });
  }
  return { add, skipped };
}

/* -------------------------------------------------------------------------------------- what the screen reads -- */

/** One row that would be added, as the preview shows it. */
export interface ListRowView {
  row: number;
  company: string;
  /** The person's whole name as the list gives it, or "" when it gives none. */
  person: string;
  role: string | null;
  email: string;
  country: string;
  domain: string;
}

/** `POST /v1/customers/list/preview`: what an import would do, before it does anything. */
export interface ListPreview {
  delimiter: ListDelimiter;
  /** Each field the list has, in the screen's words, and the column or columns it is read from. */
  read: Array<{ field: ListField; says: string; columns: string[] }>;
  /** Every column nothing here reads, named back by the name the file gives it. */
  ignored: string[];
  /** Rows in the list, the header not counted. */
  rows: number;
  /** How many would be added, and the first of them. */
  adds: number;
  add: ListRowView[];
  /** Why the rest would not be, with how many of each and a few of them. */
  skipped: Array<{ why: ListSkip; says: string; count: number; examples: string[] }>;
  /**
   * Of those added, how many are in a country no letter goes to, and why (ADR 0065). They are added, read and judged
   * all the same, for an introduction or a call, and no letter is written to them; said before the press because
   * reading them costs what reading any company costs.
   */
  held: Array<{ country: string; says: string; count: number }>;
  /**
   * The sentence the footer of the first letter would carry, so the person reads what their list will be said to be
   * before anything is added. Null when nothing would be.
   */
  footer: string | null;
  /**
   * What reading one company and writing its letter costs, from the operations' own estimates and the margin this
   * box charges (13), as a dollar string. Null where this box cannot price it. The import itself costs nothing.
   */
  perCompanyUsd: string | null;
}

/** `POST /v1/customers/list`: what was added, and the preview it was added from. */
export interface ListImported {
  added: number;
  skipped: number;
  preview: ListPreview;
}

/** How many of the rows to add the preview draws. The rest are counted. */
export const LIST_PREVIEW_ROWS = 50;

/** The preview, from a list read and sorted. Pure, so the API and a test build the same one. */
export function listPreview(
  read: Extract<ListRead, { ok: true }>,
  sorted: { add: readonly ListPerson[]; skipped: readonly ListSkipped[] },
  ctx: { source: string; language: string; perCompanyUsd: string | null },
): ListPreview {
  // The footer of a letter that will actually go: a list whose first row is in a held country gets no letter there.
  const first = sorted.add.find((p) => lettersGo(p.country).goes);
  const held = new Map<string, number>();
  for (const p of sorted.add) if (!lettersGo(p.country).goes) held.set(p.country, (held.get(p.country) ?? 0) + 1);
  const byWhy = new Map<ListSkip, ListSkipped[]>();
  for (const s of sorted.skipped) byWhy.set(s.why, [...(byWhy.get(s.why) ?? []), s]);
  return {
    delimiter: read.delimiter,
    read: read.columns.map((c) => ({ field: c.field, says: LIST_FIELD_WORDS[c.field], columns: c.headers })),
    ignored: read.ignored,
    rows: read.rows.length,
    adds: sorted.add.length,
    add: sorted.add.slice(0, LIST_PREVIEW_ROWS).map((p) => ({
      row: p.row, company: p.company, person: [p.firstName, p.lastName].filter(Boolean).join(" "),
      role: p.role, email: p.email, country: p.country, domain: p.domain,
    })),
    skipped: LIST_SKIPS.filter((why) => byWhy.has(why)).map((why) => {
      const rows = byWhy.get(why)!;
      return {
        why, says: LIST_SKIP_WORDS[why], count: rows.length,
        examples: rows.slice(0, 3).map((s) => [`row ${s.row}`, s.company || null, s.email || null].filter(Boolean).join(" · ") + (s.said ? ` (“${s.said.slice(0, 40)}”)` : "")),
      };
    }),
    held: [...held].map(([country, count]) => {
      const law = lettersGo(country);
      return { country, count, says: law.goes ? "" : law.says };
    }),
    footer: first ? letterFrame(ctx.language).listed(first.company, first.email, ctx.source) : null,
    perCompanyUsd: ctx.perCompanyUsd,
  };
}
